import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, Download, Calendar, Sparkles } from "lucide-react";
import StrategyCallModalButton from "./StrategyCallModalButton";
import { STRONGER_OPEN_EVENT } from "./StrongerProgramModal";
import { setLeadSourceOverride } from "@/lib/leadSourceOverride";
import { trackCtaClick } from "@/lib/analytics";

export type BlogLeadMagnet = "macro" | "stronger" | "planner";

type Props = {
  variant: "inline" | "footer";
  /** Only used for inline variant. Determines which existing lead flow to open. */
  magnet?: BlogLeadMagnet;
  /** The blog post slug (final path segment, no leading /blog/). Used for source attribution. */
  slug: string;
};

const inlineCopy: Record<BlogLeadMagnet, { eyebrow: string; title: string; body: string; cta: string; icon: ReactNode }> = {
  macro: {
    eyebrow: "Free Tool",
    title: "Find Your Macros (Free Calculator)",
    body: "Adult-sized, coach-built. Get the exact protein, carbs, and fats we start our members on  -  in under 60 seconds.",
    cta: "Open the Macro Calculator",
    icon: <Calculator className="w-4 h-4" />,
  },
  stronger: {
    eyebrow: "Free Workout PDF",
    title: "7 Days Stronger  -  Try It At Your Gym",
    body: "The exact Week 1 we run with adults 30-55+ at Hive. Two upper days, two lower days, real tempo. Yours to run on your own.",
    cta: "Send Me 7 Days Stronger",
    icon: <Download className="w-4 h-4" />,
  },
  planner: {
    eyebrow: "Free Weekly Planner",
    title: "The Finally-Stick-To-It Planner",
    body: "The simple weekly planner our busy-professional members use to protect training time and win the week  -  even when work gets loud.",
    cta: "Get the Free Planner",
    icon: <Download className="w-4 h-4" />,
  },
};

const BlogLeadCTA = ({ variant, magnet, slug }: Props) => {
  if (variant === "inline") {
    const m = magnet ?? "stronger";
    const copy = inlineCopy[m];
    const source = `blog_inline_${m}_${slug}`;
    const pagePath = typeof window !== "undefined" ? window.location.pathname : `/blog/${slug}`;

    const handleClick = () => {
      setLeadSourceOverride(source);
      trackCtaClick({
        cta_label: `blog_inline_${m}`,
        cta_destination:
          m === "stronger" ? "stronger_modal" : m === "macro" ? "/nutrition" : "/planner",
        page_path: pagePath,
      });
    };

    const inner = (
      <div className="not-prose my-10 rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 via-background to-background p-6 sm:p-7 shadow-lg">
        <span className="inline-flex items-center gap-1.5 text-primary font-bold text-[11px] uppercase tracking-widest">
          <Sparkles className="w-3.5 h-3.5" /> {copy.eyebrow}
        </span>
        <h3 className="font-display text-2xl sm:text-3xl mt-2 mb-2 leading-tight">
          <span className="text-gradient-honey">{copy.title}</span>
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4 max-w-xl">
          {copy.body}
        </p>
        <span className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-gradient-honey font-semibold text-sm text-primary-foreground hover:opacity-90 transition-opacity">
          {copy.icon}
          {copy.cta}
          <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    );

    if (m === "stronger") {
      return (
        <button
          type="button"
          onClick={() => {
            handleClick();
            if (typeof window !== "undefined") {
              window.dispatchEvent(new Event(STRONGER_OPEN_EVENT));
            }
          }}
          className="block w-full text-left"
          aria-label={copy.cta}
        >
          {inner}
        </button>
      );
    }

    const to = m === "macro" ? "/nutrition#macro-calculator" : "/planner#get-planner";
    return (
      <Link to={to} onClick={handleClick} className="block" aria-label={copy.cta}>
        {inner}
      </Link>
    );
  }

  // Footer variant  -  strategy call CTA
  const pagePath = typeof window !== "undefined" ? window.location.pathname : `/blog/${slug}`;
  return (
    <section className="py-14 sm:py-16 section-padding">
      <div className="max-w-3xl mx-auto rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 via-background to-background p-8 sm:p-10 text-center glow-honey-sm">
        <span className="inline-flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-widest">
          <Sparkles className="w-4 h-4" /> Ready when you are
        </span>
        <h3 className="font-display text-3xl sm:text-4xl mt-3 mb-3 leading-tight">
          <span className="text-gradient-honey">READY TO STOP STARTING OVER?</span>
        </h3>
        <p className="text-muted-foreground text-base leading-relaxed max-w-xl mx-auto mb-6">
          One 15-minute call with Coach Martin  -  we'll map what's actually working, what isn't, and the smallest next step that fits your week. No pressure, no card.
        </p>
        <StrategyCallModalButton
          surface="blog_lead_cta"
          className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-lg bg-gradient-honey font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
          onOpen={() =>
            trackCtaClick({
              cta_label: `blog_footer_strategy_call`,
              cta_destination: "strategy_call_modal",
              page_path: pagePath,
            })
          }
        >
          <Calendar className="w-4 h-4" />
          Book a Free Strategy Call
          <ArrowRight className="w-4 h-4" />
        </StrategyCallModalButton>
      </div>
    </section>
  );
};

export default BlogLeadCTA;
